import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Container } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faUserTag,
  faCrown,
  faChevronDown,
} from '@fortawesome/pro-solid-svg-icons'
import { useSelector, useDispatch } from 'react-redux'
import { useParams } from 'react-router'
import { Menu, Dropdown } from 'antd'
import Avatar from 'design-system/avatar'
import { AvatarSizes } from 'design-system/avatar/src/resources/Avatar.enum'
import Tag from 'components/ui/tag/Tag'
import { IParticipantModel } from 'models/participant.model'
import { userRolEnum } from 'enum/user-rol.enum'
import { IMainState } from 'store/states'
import * as fromCoursesActions from 'store/actions/courses.action'
import * as fromProfilesActions from 'store/actions/lms-profiles.action'
import * as fromActions from 'store/actions/participants.action'
import { LmsProfileTypes } from 'enum/lms-profile-types.enum'
import ConfirmationModal from 'components/ui/modals/confirmation/ConfirmationModal'
import styles from './UserInfo.module.scss'
import { EditSupervisors } from './EditSupervisors'
import EditProfilesModal from './components/EditProfilesModal'
import usePermissions from './hooks/usePermissions'

interface IUserInfo {
  data: IParticipantModel
  setShowPermissionsModal?: (value: boolean) => void
  showPermissionsModal?: boolean
}

interface Params {
  userId: string
}

const UserInfo: React.FC<IUserInfo> = ({ data, setShowPermissionsModal }) => {
  const { t } = useTranslation()

  const { permission } = useSelector(({ user }: IMainState) => user)
  const { admin } = userRolEnum
  const [showEditModal, setShowEditModal] = useState(false)
  const dispatch = useDispatch()

  const {
    fullName,
    identifier,
    email,
    avatar,
    area,
    position,
    lmsProfiles,
    roleId,
  } = { ...data }

  const lmsProfilesTypeParticipants = lmsProfiles?.filter(
    (profile) => profile.profileType === LmsProfileTypes.participants
  )
  const lmsProfilesTypeOthers = lmsProfiles?.filter(
    (profile) => profile.profileType !== LmsProfileTypes.participants
  )
  const [showInfoModal, setShowInfoModal] = useState(false)
  const { userId } = useParams<Params>()

  useEffect(() => {
    dispatch({ type: fromCoursesActions.COURSES_GET_NAME_LIST })
    dispatch({ type: fromProfilesActions.LMS_ALL_PROFILES_LIST })
  }, [dispatch])

  const handleSetAdmin = () => {
    setShowInfoModal(false)
    dispatch({
      type: fromActions.PARTICIPANT_EDIT_ROLE,
      payload: { user: { roleId: null }, userId },
    })
  }

  const { coursePermissions, coursesToShow, profilePermissions } =
    usePermissions()
  return (
    <>
      <Container
        fluid
        className=" d-flex justify-content-center flex-column text-center"
      >
        <Avatar
          sizes={AvatarSizes.XExtraLarge}
          className={styles.avatar}
          src={avatar}
        />
        <h1 className={styles.name}>{fullName}</h1>
        {area && <h2 className={styles.area}>{area.name}</h2>}
        <h3 className={styles.position}>{position}</h3>
        <h3 className={styles.identifierAndEmail}>{identifier}</h3>
        <h3 className={styles.identifierAndEmail}>{email}</h3>
        {!!roleId && (
          <p className={`${styles.isAdmin} mt-1`}>
            {t(
              `configuration.user.${
                roleId === admin ? 'isAdmin' : 'isModerator'
              }`
            )}
            <FontAwesomeIcon
              icon={roleId === admin ? faCrown : faUserTag}
              className={`ml-1 ${styles.adminIcon}`}
            />
          </p>
        )}
        {(permission.admin || permission.superadmin) && (
          <div className="">
            <button
              className={styles.negativePrimary}
              onClick={() => {
                setShowEditModal(true)
              }}
            >
              {t('configuration.user.editProfiles')}
            </button>
          </div>
        )}
        {(permission.admin || permission.superadmin) && (
          <div className="mt-2">
            <EditSupervisors userId={data.id} supervisors={data.supervisors} />
          </div>
        )}
        <div className={styles.coursesAndProfiles}>
          {roleId === 2 && (
            <div>
              {!!coursePermissions.length && (
                <div className="mt-1 mb-3">
                  <h2 className={styles.subtitle}>
                    {t('configuration.user.managedCourses')}
                  </h2>
                  <div>
                    {coursesToShow.map(({ selected }, index) => {
                      return (
                        selected && (
                          <p
                            key={`coursePermissionName-${index}`}
                            className={styles.courseName}
                          >
                            {selected.name}
                          </p>
                        )
                      )
                    })}
                  </div>
                  {coursePermissions.length > 3 && (
                    <div
                      className={styles.seeMore}
                      onClick={() => {
                        setShowPermissionsModal?.(true)
                      }}
                    >
                      {t('configuration.user.seeMore', {
                        courses:
                          coursePermissions.length - coursesToShow.length,
                      })}
                    </div>
                  )}
                </div>
              )}
              {!!profilePermissions.length && (
                <div className="mt-1 mb-3">
                  <h2 className={styles.subtitle}>
                    {t('configuration.user.managedProfiles')}
                  </h2>
                  <div className={styles.profiles}>
                    {profilePermissions.map(
                      ({ selected }, index) =>
                        selected && (
                          <Tag
                            key={`profilesPermissionName-${index}`}
                            className="mt-2"
                            text={selected.name}
                            color={selected.color}
                            size="sm"
                          />
                        )
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        {(permission.admin || permission.superadmin) && (
          <div className="m-2">
            <Dropdown
              className={styles.dropdownManage}
              overlay={
                <Menu>
                  <Menu.Item
                    key="0"
                    onClick={() => setShowPermissionsModal?.(true)}
                  >
                    {t(`configuration.user.managePermissions`)}
                  </Menu.Item>
                  {(permission.admin || permission.superadmin) && (
                    <Menu.Item key="1" onClick={() => setShowInfoModal(true)}>
                      {t(`configuration.user.removeAllPermissions`)}
                    </Menu.Item>
                  )}
                </Menu>
              }
              trigger={['click']}
            >
              <a
                className="ant-dropdown-link"
                onClick={(e) => e.preventDefault()}
              >
                {t('configuration.user.manageLabel')}
                <FontAwesomeIcon className="mt-1 ml-2" icon={faChevronDown} />
              </a>
            </Dropdown>
          </div>
        )}
        <div className="mt-1">
          <div>
            <h2 className={styles.subtitle}>
              {t('configuration.user.assignedProfilesByColaborator')}
            </h2>
            <div className={styles.profiles}>
              {lmsProfilesTypeParticipants &&
                lmsProfilesTypeParticipants.map((profile) => (
                  <Tag
                    className={`mt-2`}
                    key={profile.name}
                    text={profile.name}
                    color={profile.color}
                    size="sm"
                  />
                ))}
            </div>
          </div>
          {!!lmsProfilesTypeOthers?.length && (
            <div className="mt-2">
              <h2 className={styles.subtitle}>
                {t('configuration.user.assignedProfilesByEnterprise')}
              </h2>
              <div className={styles.profiles}>
                {lmsProfilesTypeOthers &&
                  lmsProfilesTypeOthers.map((profile) => (
                    <Tag
                      className={`mt-2`}
                      key={profile.name}
                      text={profile.name}
                      color={profile.color}
                      size="sm"
                    />
                  ))}
              </div>
            </div>
          )}
        </div>
      </Container>
      <EditProfilesModal
        show={showEditModal}
        initialState={lmsProfilesTypeParticipants}
        userId={data.id}
        onClose={() => {
          setShowEditModal(false)
        }}
      />
      {/* Remove Permissions */}
      <ConfirmationModal
        show={showInfoModal}
        acceptButtonText={t('configuration.user.resetModal.buttons.accept')}
        text={t(`configuration.user.removePermissionsMessage`)}
        onAccept={handleSetAdmin}
        onClose={() => setShowInfoModal(false)}
      />
    </>
  )
}

export default UserInfo
